// @owners { team: patients }
import { H2, Icon, ListItem, MdSpacing, Tag } from '@alto/design-system';
import React, { useCallback, useEffect, useState } from 'react';
import { getCompanyName } from '~shared/config/getCompanyName';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useFetchAppDownloadLink } from '~shared/queries/useFetchAppDownloadLink';
import { Drawer } from '~web/components/Drawer';
import { Image as ImageComponent } from '~web/components/Image';
import browserIcon from '~web/images/browser-icon_spot-illo@3x.png';
import appAlto from '~web/images/notification@2x.png';

export type Props = {
  readonly onClose: () => void;
  readonly tempPassword: string;
};

export const GetTheAppDrawer = ({ onClose, tempPassword }: Props) => {
  const { trackEvent } = useAnalytics();
  const { mutateAsync: fetchAppDownloadLink } = useFetchAppDownloadLink();
  const [appDownloadUrl, setAppDownloadUrl] = useState<string | null>(null);
  const [appDownloadClicked, setAppDownloadClicked] = useState(false);

  const handleDownloadApp = useCallback(async () => {
    const url = await fetchAppDownloadLink(tempPassword);
    setAppDownloadUrl(url);
    // Open the link immediately if the user wanted to open the link but it wasn't available yet
    if (appDownloadClicked) {
      window.location.assign(url);
    }
  }, [appDownloadClicked, fetchAppDownloadLink, tempPassword]);

  useEffect(() => {
    handleDownloadApp();
  }, [handleDownloadApp]);

  const handleAppSelected = () => {
    trackEvent({ event: EVENTS.UPSELL_GET_THE_APP_TAPPED });
    onClose();

    if (appDownloadUrl) {
      window.location.assign(appDownloadUrl);
    } else {
      setAppDownloadClicked(true);
    }
  };

  const handleBrowserSelected = () => {
    trackEvent({ event: EVENTS.UPSELL_CONTINUE_WITH_BROWSER });
    onClose();
  };

  return (
    <Drawer
      analyticsName="how_to_use_alto"
      onClose={onClose}
      dismissible
    >
      <H2>How do you want to use {getCompanyName()}?</H2>
      <MdSpacing />
      <ListItem
        onPress={handleAppSelected}
        LeftContent={
          <ImageComponent
            alt="Alto App Logo"
            src={appAlto}
            sidebar
          />
        }
        title="Download mobile app"
        tags={
          <Tag
            label="Recommended"
            type="recommended"
          />
        }
        tagPlacement="bottom"
        RightContent={
          <Icon
            name="chevronright"
            type="grey"
          />
        }
      />
      <ListItem
        onPress={handleBrowserSelected}
        LeftContent={
          <ImageComponent
            alt="Browser Logo"
            src={browserIcon}
            sidebar
          />
        }
        title="Continue on web"
        RightContent={
          <Icon
            name="chevronright"
            type="grey"
          />
        }
      />
    </Drawer>
  );
};
